
import { useState } from 'react';

interface CaseStudyCardProps {
  study: {
    id: string;
    title: string;
    client: string;
    location: string;
    acreage: string;
    crop: string;
    category: string;
    challenge: string;
    solution: string;
    results: { metric: string; label: string }[];
    testimonial: string;
    testimonialAuthor: string;
    image: string;
    resultImage: string;
  };
  index: number;
}

export default function CaseStudyCard({ study, index }: CaseStudyCardProps) {
  const [activeTab, setActiveTab] = useState<'challenge' | 'solution'>('challenge');
  const isReversed = index % 2 !== 0;

  return (
    <article className="bg-white rounded-lg shadow-sm hover:shadow-lg transition-shadow overflow-hidden">
      {/* Header with images */}
      <div className={`grid md:grid-cols-2 ${isReversed ? 'md:direction-rtl' : ''}`}>
        <div className={`relative ${isReversed ? 'md:order-2' : ''}`}>
          <div className="w-full h-[320px]">
            <img
              src={activeTab === 'challenge' ? study.image : study.resultImage}
              alt={`${study.title} - ${activeTab === 'challenge' ? 'before' : 'after'}`}
              className="w-full h-full object-cover object-top transition-opacity duration-500"
            />
          </div>
          <div className="absolute top-4 left-4">
            <span className="inline-block bg-emerald-800 text-white text-xs font-bold px-3 py-1.5 rounded-full tracking-wide uppercase whitespace-nowrap">
              {study.category}
            </span>
          </div>
        </div>

        <div className={`p-8 flex flex-col justify-center ${isReversed ? 'md:order-1' : ''}`}>
          <h3 className="text-2xl font-bold mb-3 tracking-wide leading-tight">{study.title}</h3>
          <div className="flex flex-wrap gap-4 mb-5 text-sm text-gray-600">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line text-emerald-700"></i>
              {study.client}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-map-pin-line text-emerald-700"></i>
              {study.location}
            </span>
          </div>
          <div className="flex flex-wrap gap-4 mb-5 text-sm text-gray-600">
            <span className="flex items-center gap-1.5">
              <i className="ri-landscape-line text-emerald-700"></i>
              {study.acreage}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-plant-line text-emerald-700"></i>
              {study.crop}
            </span>
          </div>

          {/* Tab switcher */}
          <div className="flex bg-gray-100 rounded-full p-1 mb-5">
            <button
              onClick={() => setActiveTab('challenge')}
              className={`flex-1 py-2 px-4 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'challenge'
                  ? 'bg-emerald-800 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              The Challenge
            </button>
            <button
              onClick={() => setActiveTab('solution')}
              className={`flex-1 py-2 px-4 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'solution'
                  ? 'bg-emerald-800 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Our Solution
            </button>
          </div>

          <p className="text-gray-700 leading-relaxed text-sm">
            {activeTab === 'challenge' ? study.challenge : study.solution}
          </p>
        </div>
      </div>

      {/* Results grid */}
      <div className="border-t border-gray-100 px-8 py-6">
        <h4 className="text-sm font-bold text-emerald-700 uppercase tracking-widest mb-4">
          <a href={`#results-${study.id}`} id={`results-${study.id}`}>Measured Results</a>
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {study.results.map((result, i) => (
            <div key={i} className="bg-stone-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-emerald-800 mb-1">{result.metric}</div>
              <p className="text-xs text-gray-600 font-medium leading-snug">{result.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Testimonial */}
      <div className="border-t border-gray-100 px-8 py-6 bg-stone-50/50">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-double-quotes-l text-3xl text-emerald-700/30"></i>
          </div>
          <div>
            <p className="text-gray-700 italic leading-relaxed text-sm mb-2">
              {study.testimonial}
            </p>
            <p className="text-sm font-bold text-gray-900">— {study.testimonialAuthor}</p>
          </div>
        </div>
      </div>
    </article>
  );
}
